import { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, TrendingUp, TrendingDown, RefreshCw, Loader2, Globe2, AlertTriangle, FileText, Award, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function DailyReportPage() {
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    document.title = "Daily Report | Epidemos Admin";
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/daily-report/latest`),
        axios.get(`${BACKEND_URL}/api/admin/daily-report/history?limit=14`),
      ]);
      if (r.data.ok) setReport(r.data.report);
      setHistory(h.data.reports || []);
    } finally { setLoading(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/daily-report/generate`);
      load();
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Ładuję…</div>;

  return (
    <div className="p-6 max-w-none mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="w-7 h-7 text-blue-600" /> Daily Performance Report
          </h1>
          <p className="text-sm text-slate-500">Codzienny raport agentów (cron 06:00). Co opublikowano w 24h + trend pozycji + alerty.</p>
        </div>
        <div className="flex gap-2">
          <a href={`${BACKEND_URL}/api/admin/export/looker-studio?format=csv`}
            className="px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg inline-flex items-center gap-1.5">
            <Download className="w-4 h-4" /> Eksport CSV (Looker Studio)
          </a>
          <button onClick={generate} disabled={generating}
            className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Wygeneruj teraz
          </button>
        </div>
      </div>

      {!report ? (
        <div className="p-6 bg-amber-50 border border-amber-200 rounded">Brak raportu — kliknij "Wygeneruj teraz"</div>
      ) : (
        <>
          {/* Top stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="p-4 bg-white border border-slate-200 rounded-xl">
              <div className="text-xs text-slate-500">Publikacje 24h</div>
              <div className="text-3xl font-bold text-slate-900">{report.publikacje_24h}</div>
              <div className="text-xs text-slate-500 mt-1">{report.publikacje_7d} / 7d</div>
            </div>
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="text-xs text-green-700">Rosnące pozycje ↑</div>
              <div className="text-3xl font-bold text-green-700">{report.rank_rising_count}</div>
              <div className="text-xs text-green-600 mt-1">vs poprzedni check</div>
            </div>
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="text-xs text-red-700">Spadające pozycje ↓</div>
              <div className="text-3xl font-bold text-red-700">{report.rank_falling_count}</div>
              <div className="text-xs text-red-600 mt-1">wymaga uwagi</div>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="text-xs text-amber-700">Otwarte alerty</div>
              <div className="text-3xl font-bold text-amber-700">{report.open_alerts}</div>
              <div className="text-xs text-amber-600 mt-1">+{report.new_topics_24h} nowych tematów</div>
            </div>
          </div>

          {/* Top portali + trend */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="font-bold mb-3 flex items-center gap-2"><Globe2 className="w-5 h-5" /> TOP 5 portali (24h)</h2>
              <table className="w-full text-sm">
                <tbody>
                  {report.top5_portali_24h?.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-2 text-slate-400 w-8">{i + 1}.</td>
                      <td className="py-2 font-medium">{p.site}</td>
                      <td className="py-2 text-right font-mono font-bold">{p.posts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="font-bold mb-3 flex items-center gap-2"><Award className="w-5 h-5 text-purple-600" /> Quality Distribution (last 500)</h2>
              {report.quality_distribution_last500 && (
                <div className="space-y-2">
                  {Object.entries(report.quality_distribution_last500).map(([range, count]) => {
                    const total = Object.values(report.quality_distribution_last500).reduce((a, b) => a + b, 0);
                    const pct = total ? Math.round(count / total * 100) : 0;
                    const color = range === "0-25" ? "bg-red-500" : range === "26-50" ? "bg-amber-500" : range === "51-75" ? "bg-blue-500" : "bg-green-500";
                    return (
                      <div key={range} className="flex items-center gap-3 text-sm">
                        <div className="w-12 text-slate-600">{range}</div>
                        <div className="flex-1 bg-slate-100 rounded h-5 overflow-hidden">
                          <div className={`h-full ${color}`} style={{ width: `${pct}%` }}></div>
                        </div>
                        <div className="w-20 text-right text-slate-600">{count} ({pct}%)</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Rising + Falling */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-5">
              <h2 className="font-bold mb-3 flex items-center gap-2 text-green-900"><TrendingUp className="w-5 h-5" /> TOP rosnące keyword (10)</h2>
              {report.rank_top_rising?.length > 0 ? (
                <ul className="text-sm space-y-1">
                  {report.rank_top_rising.slice(0, 10).map((k, i) => (
                    <li key={i} className="text-slate-700">
                      <span className="text-green-600 font-mono">{k.from} → {k.to}</span> <strong>{k.keyword}</strong>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-sm text-slate-500">Brak danych — czekaj na rank-tracker cron 03:30</div>}
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-5">
              <h2 className="font-bold mb-3 flex items-center gap-2 text-red-900"><TrendingDown className="w-5 h-5" /> TOP spadające keyword (10)</h2>
              {report.rank_top_falling?.length > 0 ? (
                <ul className="text-sm space-y-1">
                  {report.rank_top_falling.slice(0, 10).map((k, i) => (
                    <li key={i} className="text-slate-700">
                      <span className="text-red-600 font-mono">{k.from} → {k.to}</span> <strong>{k.keyword}</strong>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-sm text-slate-500">Brak danych</div>}
            </div>
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="font-bold mb-3 flex items-center gap-2"><FileText className="w-5 h-5" /> Historia 14 dni</h2>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="text-left p-2">Data</th>
                    <th className="text-right p-2">Posty 24h</th>
                    <th className="text-right p-2">Rising</th>
                    <th className="text-right p-2">Falling</th>
                    <th className="text-right p-2">Alerty</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} className="border-t border-slate-100">
                      <td className="p-2 font-mono text-slate-600">{(h.generated_at || "").substring(0, 10)}</td>
                      <td className="p-2 text-right font-bold">{h.publikacje_24h}</td>
                      <td className="p-2 text-right text-green-600">{h.rank_rising_count}</td>
                      <td className="p-2 text-right text-red-600">{h.rank_falling_count}</td>
                      <td className="p-2 text-right">{h.open_alerts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
